/**
 * Plays a how-to demo clip (src/game/howto/demo.ts) in the game's own world and view: the ride in progress is
 * put aside (bike, clock, ruts and knocked cones), the expert's inputs are stepped at 240 Hz like a replay, and a
 * side / chase camera follows. Stopping puts the ride back exactly as it was.
 */
import * as THREE from 'three/webgpu';
import { clipSteps, DemoClip, DemoMark } from '../game/howto/demo';
import { PristineSource, pristineTerrain } from '../game/howto/pristine';
import { GameWorld, WorldSnapshot } from '../game/world';
import { brakeTorques, BikeState } from '../sim/bike';
import { decodeInput, InputFrame, INPUT_STRIDE_V2, NEUTRAL_INPUT } from '../sim/input';
import type { PrettyView } from '../view/prettyView';

/** What the overlay draws for the current demo step. */
export interface DemoOverlay {
  throttle: number;
  front: number;
  rear: number;
  body: number;
  standing: boolean;
  foot: boolean;
  progress: number;
  slow: boolean;
  /** Marks near the current step, with their screen position (null when behind the camera). */
  marks: { kind: DemoMark['kind']; x: number; y: number; visible: boolean; fade: number }[];
}

/** Seconds before / after a mark's step that it is shown. */
const MARK_BEFORE = 1.6, MARK_AFTER = 0.9;
export const SLOW_SPEED = 0.35;
/** The key moment plays slower by itself: this speed within KEY_WINDOW s of a mark (owner decision E). */
export const KEY_SPEED = 0.5, KEY_WINDOW = 0.6;

export class DemoPlayer {
  index = 0;
  slow = false;
  lastInput: InputFrame = NEUTRAL_INPUT;
  private readonly steps: number;
  private saved: WorldSnapshot | null = null;
  private restoreTerrain: (() => void) | null = null;
  private readonly camPos = new THREE.Vector3();
  private readonly camLook = new THREE.Vector3();
  private readonly tmp = new THREE.Vector3();
  private snapCam = true;

  /** Ridden chunks' untouched heights, prepared off the main thread (src/app/pristineCache.ts). */
  static pristine: PristineSource | undefined;

  constructor(readonly clip: DemoClip, readonly world: GameWorld, readonly view: PrettyView) {
    this.steps = clipSteps(clip);
  }

  get done(): boolean { return this.index >= this.steps; }
  get speed(): number { return this.slow ? SLOW_SPEED : this.nearMark() ? KEY_SPEED : 1; }

  /** True around a mark's step (the key moment of the clip). */
  nearMark(): boolean {
    const w = KEY_WINDOW / this.clip.dt;
    return this.clip.marks.some((m) => Math.abs(m.step - this.index) < w);
  }

  /** Puts the ride aside and moves the bike to the clip's start. */
  begin(): void {
    const w = this.world;
    if (!this.saved) this.saved = w.snapshot(); // bike, clock, assist plan, last input, ground
    this.restoreTerrain?.();
    this.restoreTerrain = pristineTerrain(w.terrain, DemoPlayer.pristine);
    w.respawn(this.clip.at);
    this.index = 0;
    this.snapCam = true;
    this.view.cameraOverride = (cam, dt) => this.camera(cam, dt);
  }

  /** From the start again (the "watch again" button). */
  restart(): void { this.begin(); }

  step(): void {
    if (this.done) return;
    const input = decodeInput(this.clip.inputs, this.index * INPUT_STRIDE_V2);
    this.lastInput = input;
    this.world.step(input);
    this.index++;
  }

  /** Brings the ride back as it was before the demo. */
  end(): void {
    const w = this.world, s = this.saved;
    this.restoreTerrain?.();
    this.restoreTerrain = null;
    if (s) { w.restore(s); w.release(s); }
    this.saved = null;
    this.view.cameraOverride = null;
  }

  overlay(): DemoOverlay {
    const i = this.lastInput, cfg = this.world.cfg;
    const tq = brakeTorques(i, cfg);
    const cam = this.view.camera;
    const dt = this.clip.dt;
    const marks: DemoOverlay['marks'] = [];
    for (const m of this.clip.marks) {
      const ds = (m.step - this.index) * dt;
      if (ds > MARK_BEFORE || ds < -MARK_AFTER) continue;
      this.tmp.set(m.x, m.y + 0.9, m.z).project(cam);
      const visible = this.tmp.z < 1 && Math.abs(this.tmp.x) < 1.1 && Math.abs(this.tmp.y) < 1.1;
      const fade = ds > 0 ? 1 - ds / MARK_BEFORE : 1 + ds / MARK_AFTER;
      marks.push({ kind: m.kind, x: (this.tmp.x + 1) / 2, y: (1 - this.tmp.y) / 2, visible, fade: Math.max(0, Math.min(1, fade * 1.6)) });
    }
    return {
      throttle: i.throttle, front: tq.front / cfg.frontBrakeTorque, rear: tq.rear / cfg.rearBrakeTorque, body: i.body,
      standing: i.standing, foot: this.world.bike.rider.foot > 0.5, progress: this.index / this.steps, slow: this.slow, marks,
    };
  }

  /** Side / chase camera: off the rider's right shoulder, a little behind and above, eased. */
  private camera(cam: THREE.PerspectiveCamera, dt: number): void {
    const b = this.world.bike, p = b.position, q = b.orientation;
    // Heading from the bike's forward axis (yaw only).
    const fx = 2 * (q.x * q.z + q.y * q.w), fz = 1 - 2 * (q.x * q.x + q.y * q.y);
    const fl = Math.hypot(fx, fz) || 1;
    const hx = fx / fl, hz = fz / fl;
    const rx = -hz, rz = hx; // right of the heading
    const target = this.tmp.set(p.x - hx * 3.2 - rx * 5.2, p.y + 1.7, p.z - hz * 3.2 - rz * 5.2);
    const ground = this.world.terrain.heightAt(target.x, target.z) + 0.6;
    target.y = Math.max(target.y, ground);
    const k = this.snapCam ? 1 : 1 - Math.exp(-dt * 3.5);
    this.camPos.lerp(target, k);
    if (this.snapCam) this.camPos.copy(target);
    const look = this.tmp.set(p.x + hx * 1.2, p.y + 0.4, p.z + hz * 1.2);
    this.camLook.lerp(look, this.snapCam ? 1 : 1 - Math.exp(-dt * 8));
    this.snapCam = false;
    cam.position.copy(this.camPos);
    cam.lookAt(this.camLook);
    if (cam.fov !== 50) { cam.fov = 50; cam.updateProjectionMatrix(); }
  }
}
